/**
 * Servicio de gestión de impuestos por cliente.
 * Maneja la relación many-to-many entre clientes y catalogo_impuestos.
 * Requiere cliente autenticado de Supabase como primer parámetro.
 *
 * @example
 * const { supabase } = useSupabase()
 * await impuestosClienteService.listarPorCliente(supabase!, clienteId)
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { ImpuestoClienteConCatalogo } from '@/types/vencimientos'

// Re-exportar tipos para compatibilidad
export type { ImpuestoClienteConCatalogo }

// Tipo para insertar (simplificado para la API del servicio)
export interface ImpuestoClienteInsert {
  impuesto_id: string
  numero_inscripcion?: string | null
  fecha_inscripcion?: string | null
  alicuota?: number | null
  regimen?: string | null
  observaciones?: string | null
}

// Tipo para actualizar (simplificado para la API del servicio)
export interface ImpuestoClienteUpdate {
  numero_inscripcion?: string | null
  fecha_inscripcion?: string | null
  fecha_baja?: string | null
  alicuota?: number | null
  regimen?: string | null
  activo?: boolean
  observaciones?: string | null
}

// -----------------------------------------
// Listar impuestos de un cliente
// -----------------------------------------
export async function listarImpuestosPorCliente(
  client: SupabaseClient<Database>,
  clienteId: string,
  soloActivos: boolean = true
): Promise<ImpuestoClienteConCatalogo[]> {
  let query = client
    .from('impuestos_cliente')
    .select(`
      *,
      impuesto:catalogo_impuestos(id, codigo, nombre, nombre_corto, categoria, color)
    `)
    .eq('cliente_id', clienteId)
    .order('created_at', { ascending: true })

  if (soloActivos) {
    query = query.eq('activo', true)
  }

  const { data, error } = await query

  if (error) throw error
  return (data as unknown as ImpuestoClienteConCatalogo[]) ?? []
}

// -----------------------------------------
// Listar impuestos por múltiples clientes (batch)
// -----------------------------------------
export async function listarImpuestosPorClientes(
  client: SupabaseClient<Database>,
  clienteIds: string[]
): Promise<Map<string, ImpuestoClienteConCatalogo[]>> {
  if (clienteIds.length === 0) return new Map()

  const { data, error } = await client
    .from('impuestos_cliente')
    .select(`
      *,
      impuesto:catalogo_impuestos(id, codigo, nombre, categoria, nombre_corto, color)
    `)
    .in('cliente_id', clienteIds)
    .eq('activo', true)
    .order('created_at', { ascending: true })

  if (error) throw error

  const map = new Map<string, ImpuestoClienteConCatalogo[]>()
  for (const row of (data as unknown as ImpuestoClienteConCatalogo[]) ?? []) {
    const arr = map.get(row.cliente_id) ?? []
    arr.push(row)
    map.set(row.cliente_id, arr)
  }
  return map
}

// -----------------------------------------
// Asignar impuesto a cliente
// -----------------------------------------
export async function asignarImpuesto(
  client: SupabaseClient<Database>,
  clienteId: string,
  input: ImpuestoClienteInsert
): Promise<ImpuestoClienteConCatalogo> {
  const { data, error } = await client
    .from('impuestos_cliente')
    .insert({
      cliente_id: clienteId,
      impuesto_id: input.impuesto_id,
      numero_inscripcion: input.numero_inscripcion ?? null,
      fecha_inscripcion: input.fecha_inscripcion ?? null,
      alicuota: input.alicuota ?? null,
      regimen: input.regimen ?? null,
      observaciones: input.observaciones ?? null,
      activo: true,
    })
    .select(`
      *,
      impuesto:catalogo_impuestos(id, codigo, nombre, categoria)
    `)
    .single()

  if (error) throw error
  return data as unknown as ImpuestoClienteConCatalogo
}

// -----------------------------------------
// Asignar múltiples impuestos a cliente
// -----------------------------------------
export async function asignarMultiplesImpuestos(
  client: SupabaseClient<Database>,
  clienteId: string,
  impuestoIds: string[],
  fechasInscripcion?: Record<string, string | null>
): Promise<void> {
  if (impuestoIds.length === 0) return

  const registros = impuestoIds.map((impuesto_id) => ({
    cliente_id: clienteId,
    impuesto_id,
    fecha_inscripcion: fechasInscripcion?.[impuesto_id] ?? null,
    activo: true,
  }))

  const { error } = await client
    .from('impuestos_cliente')
    .upsert(registros, {
      onConflict: 'cliente_id,impuesto_id',
      ignoreDuplicates: false,
    })

  if (error) throw error
}

// -----------------------------------------
// Desasignar impuesto de cliente (soft delete)
// -----------------------------------------
export async function desasignarImpuesto(
  client: SupabaseClient<Database>,
  clienteId: string,
  impuestoId: string
): Promise<void> {
  const { error } = await client
    .from('impuestos_cliente')
    .update({
      activo: false,
      fecha_baja: new Date().toISOString().split('T')[0],
      updated_at: new Date().toISOString(),
    })
    .eq('cliente_id', clienteId)
    .eq('impuesto_id', impuestoId)

  if (error) throw error
}

// -----------------------------------------
// Actualizar impuesto de cliente
// -----------------------------------------
export async function actualizarImpuestoCliente(
  client: SupabaseClient<Database>,
  id: string,
  input: ImpuestoClienteUpdate
): Promise<ImpuestoClienteConCatalogo> {
  const { data, error } = await client
    .from('impuestos_cliente')
    .update({
      ...input,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select(`
      *,
      impuesto:catalogo_impuestos(id, codigo, nombre, categoria)
    `)
    .single()

  if (error) throw error
  return data as unknown as ImpuestoClienteConCatalogo
}

// -----------------------------------------
// Sincronizar impuestos de cliente
// Recibe lista de impuesto_ids y sincroniza con la BD
// -----------------------------------------
export async function sincronizarImpuestosCliente(
  client: SupabaseClient<Database>,
  clienteId: string,
  impuestoIds: string[],
  fechasInscripcion?: Record<string, string | null>
): Promise<void> {
  // Obtener impuestos actuales del cliente
  const actuales = await listarImpuestosPorCliente(client, clienteId, false)
  const idsActuales = actuales.map((ic) => ic.impuesto_id)

  // Determinar qué agregar y qué desactivar
  const idsAgregar = impuestoIds.filter((id) => !idsActuales.includes(id))
  const idsDesactivar = idsActuales.filter((id) => !impuestoIds.includes(id))
  const idsReactivar = impuestoIds.filter((id) => {
    const actual = actuales.find((a) => a.impuesto_id === id)
    return actual && !actual.activo
  })

  // Agregar nuevos (con fecha de inscripción del padrón si vino — ORR-152)
  if (idsAgregar.length > 0) {
    await asignarMultiplesImpuestos(client, clienteId, idsAgregar, fechasInscripcion)
  }

  // Desactivar los que ya no están
  for (const id of idsDesactivar) {
    await desasignarImpuesto(client, clienteId, id)
  }

  // Reactivar los que estaban inactivos pero ahora están en la lista
  for (const id of idsReactivar) {
    const registro = actuales.find((a) => a.impuesto_id === id)
    if (registro) {
      await client
        .from('impuestos_cliente')
        .update({
          activo: true,
          fecha_baja: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', registro.id)
    }
  }

  // ORR-152: completar fecha de inscripción del padrón en impuestos YA existentes que
  // no la tengan (caso re-consulta ARCA de un cliente ya cargado). Solo si está vacía:
  // no pisar una fecha cargada manualmente.
  if (fechasInscripcion) {
    for (const actual of actuales) {
      const fecha = fechasInscripcion[actual.impuesto_id]
      if (fecha && !actual.fecha_inscripcion && impuestoIds.includes(actual.impuesto_id)) {
        await client
          .from('impuestos_cliente')
          .update({ fecha_inscripcion: fecha, updated_at: new Date().toISOString() })
          .eq('id', actual.id)
      }
    }
  }
}

// -----------------------------------------
// Eliminar impuesto de cliente (hard delete)
// -----------------------------------------
export async function eliminarImpuestoCliente(
  client: SupabaseClient<Database>,
  id: string
): Promise<void> {
  const { error } = await client
    .from('impuestos_cliente')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// -----------------------------------------
// Objeto exportado para compatibilidad
// -----------------------------------------
export const impuestosClienteService = {
  listarPorCliente: listarImpuestosPorCliente,
  listarPorClientes: listarImpuestosPorClientes,
  asignar: asignarImpuesto,
  asignarMultiples: asignarMultiplesImpuestos,
  desasignar: desasignarImpuesto,
  actualizar: actualizarImpuestoCliente,
  sincronizar: sincronizarImpuestosCliente,
  eliminar: eliminarImpuestoCliente,
}
